import { useLayoutEffect, useRef, useState } from 'react';

type SizeSelector<T> = (size: { width: number; height: number }) => T;

const useElementSizeSelector = <T>(selector: SizeSelector<T>) => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<T | null>(null);
  const selectorRef = useRef<SizeSelector<T>>(selector);

  useLayoutEffect(() => {
    selectorRef.current = selector;
  }, [selector]);

  useLayoutEffect(() => {
    if (!element) return undefined;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        setValue(selectorRef.current(rect));
      }
    });

    observer.observe(element);
    setValue(
      selectorRef.current({
        get width() {
          return element.clientWidth;
        },
        get height() {
          return element.clientHeight;
        },
      }),
    );
    return () => {
      observer.disconnect();
    };
  }, [element]);

  return [setElement, value] as const;
};

export default useElementSizeSelector;
